import { Component, EventEmitter, Inject, Input, LOCALE_ID, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from "@angular/core";
import { SnackBarService } from "@teammood/shared/ui/snack-bar";
import { UserService, UserTeam } from "@teammood/shared/util/user";
import { catchError, EMPTY, filter, mergeMap, Subject, takeUntil, tap } from "rxjs";
import { PollSetupService } from "../polls-setup.service";
import { PollInTeam, PollList, PollListItem, PollOccurrence } from "./user-polls.type";
import { UserPollsService } from "./user-polls.service";
import { format, getDaysInMonth, getWeek } from "date-fns";
import { TranslateService } from "@ngx-translate/core";
import { DatePipe, ViewportScroller } from '@angular/common';
import { RecurrenceType } from "../create-poll/create-poll.type";
import { ActivatedRoute } from "@angular/router";
import { TaskBookBacklogDialogService } from "../../taskbook-backlog/taskbook-backlog-dialog.service";


@Component({
    selector: "user-polls",
    templateUrl: "./user-polls.component.html",
    styleUrls: ["./user-polls.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UserPollsComponent implements OnInit, OnDestroy {

    teams: UserTeam[] = [];
    userName: string;
    locale: string;
    datePipe: DatePipe;

    @Input() polls: PollList;
    @Input() searchTerm: string;
    @Output() reloadPolls : EventEmitter<boolean> = new EventEmitter();
    @Output() deletePoll : EventEmitter<PollOccurrence> = new EventEmitter();
    
    constructor(
        @Inject(LOCALE_ID) locale: string,
        private pollSetupService: PollSetupService,
        private userPollsService: UserPollsService,
        private snackBarService: SnackBarService,
        private translate: TranslateService,
        private viewportScroller: ViewportScroller,
        private route: ActivatedRoute,
        private taskbookBacklogDialogService: TaskBookBacklogDialogService,
        private userService: UserService
    ){
        this.locale = locale;
        this.datePipe = new DatePipe(this.locale);
    }

    ngOnInit() {
        this.userService.get().then(async (user) => {
            if (user) {
                this.teams = [...user.teams];
            }
        })
        //if anchor is set, scroll to it (used for notification links)
        const hash = this.route.snapshot.queryParams['poll'];

        if (hash) {
            setTimeout(() => {
                this.viewportScroller.setOffset([0, 112]);
                this.viewportScroller.scrollToAnchor(hash);
            }, 1000);
        }
    }

    private _destroy$ = new Subject<void>();

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    protected async remove(poll: PollOccurrence){
        this.deletePoll.emit(poll);
    }

    protected async showTrendsInPollResultDialog(poll: PollListItem){
        const pollDialogRef = await this.taskbookBacklogDialogService.pollTaskDetails(poll.id, this.teams, 'setup', 'trends');
        pollDialogRef.beforeClosed().pipe(
            takeUntil(pollDialogRef.afterClosed())
        ).subscribe();
    }

    protected async makePollRecurrent(poll: PollListItem, frequency: RecurrenceType){
        const dialogRef = await this.userPollsService.openMakeRecurrentDialog(poll, frequency);
        dialogRef.beforeClosed().pipe(
            filter(poll => !!poll),
            mergeMap((poll: PollListItem) => {
                return this.pollSetupService.makePollRecurrent(poll.id, frequency, poll.duration).pipe(
                    catchError((err) => {
                        this.snackBarService.error(JSON.stringify(err));
                        throw err;
                    }),
                    tap(() => {
                        this.snackBarService.success('polls_settings.make_recurrent_dialog.success');
                        this.reloadPolls.emit(true);
                    })
                )
            }),
            takeUntil(this._destroy$)
        ).subscribe();
    }

    protected toggle_recurrence(poll: PollListItem){
        poll.recurrence.enabled = !poll.recurrence.enabled;
        this.pollSetupService.updatePollRecurrence(poll, poll.recurrence.enabled)
            .pipe(
                takeUntil(this._destroy$)
            )
            .subscribe();
    }

    protected getFrequencyStartDay(startDate: Date, type: string){
        return this.userPollsService.getFrequencyStartDay(startDate, type);
    }

   
}